/**
 * Administration Session Manager
 * 
 * Main component for managing two-step medication administration sessions.
 * Integrates with the patient context medpass workflow to provide:
 * - Session creation and management
 * - Start/End administration processes
 * - 24-hour window validation
 * - Nurse handoff capabilities
 * - Real-time session monitoring
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
    Card,
    Button,
    Alert,
    Space,
    Tag,
    Descriptions,
    Modal,
    message,
    Tooltip,
    Badge,
    Spin,
    Row,
    Col
} from 'antd';
import {
    PlayCircleOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    ClockCircleOutlined,
    UserOutlined,
    MedicineBoxOutlined,
    ExclamationCircleOutlined,
    InfoCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';

import StartAdministrationModal from './StartAdministrationModal';
import EndAdministrationModal from './EndAdministrationModal';
import SessionDetailsModal from './SessionDetailsModal';
import AdministrationHistoryButton from './AdministrationHistoryButton';
import { validateAdministrationWindow } from '../../../utils/administrationValidation';
import config from '../../../config/config';

dayjs.extend(duration);
dayjs.extend(relativeTime);

const AdministrationSessionManager = ({
    patient,
    medication,
    visible,
    onClose,
    onComplete
}) => {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showStartModal, setShowStartModal] = useState(false);
    const [showEndModal, setShowEndModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedSession, setSelectedSession] = useState(null);
    const [currentTime, setCurrentTime] = useState(dayjs());

    // Enhanced state for smart medication dashboard
    const [visibleMedications, setVisibleMedications] = useState([]);
    const [dashboardSummary, setDashboardSummary] = useState(null);
    const [selectedMedication, setSelectedMedication] = useState(null);

    // Update current time every minute for real-time validation
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(dayjs());
        }, 60000);
        
        return () => clearInterval(timer);
    }, []);

    // Load existing sessions and smart medication dashboard
    useEffect(() => {
        if (visible && patient) {
            loadAdministrationSessions();
            loadMedicationDashboard();
        }
    }, [visible, patient]);

    // Auto-start administration when component opens with medication
    useEffect(() => {
        if (visible && medication && patient) {
            console.log('🎯 Auto-starting administration for medication:', medication);
            // Automatically create and start a session for the selected medication
            createAdministrationSession(medication);
        }
    }, [visible, medication, patient]);

    const loadAdministrationSessions = async () => {
        console.log('📋 loadAdministrationSessions called for medication:', medication?.medication_id);
        setLoading(true);
        try {
            // Call the real API to get administration sessions
            const response = await fetch(`${config.serverPath}/medpass/administration-sessions?medication_id=${medication.medication_id}&patient_id=${patient.id}&session_status=started`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${JSON.parse(localStorage.getItem('loginSuccessNeurulaHealth'))?.authToken || ''}`
                }
            });
            
            if (response.ok) {
                const data = await response.json();
                // Only show sessions that are in progress/started
                const filtered = (data.sessions || []).filter(s => ['started', 'in_progress'].includes(s.status));
                console.log('📋 Loaded sessions:', filtered);
                setSessions(filtered);
            } else {
                console.error('Failed to load administration sessions:', response.status);
                // Fallback to empty array if API fails
                setSessions([]);
            }
        } catch (error) {
            console.error('Error loading sessions:', error);
            // Fallback to empty array if API fails
            setSessions([]);
        } finally {
            setLoading(false);
        }
    };

    const loadMedicationDashboard = async () => {
        try {
            const response = await fetch(`${config.serverPath}/medpass/patient-context/patients/${patient.id}/medications-dashboard`, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${JSON.parse(localStorage.getItem('loginSuccessNeurulaHealth'))?.authToken || ''}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setVisibleMedications(data.medications || []);
                setDashboardSummary(data.summary || {});
            } else {
                console.error('Failed to load medication dashboard:', response.status);
            }
        } catch (error) {
            console.error('Error loading medication dashboard:', error);
        }
    };

    const createAdministrationSession = async (selectedMed = null) => {
        console.log('📝 createAdministrationSession called with:', selectedMed);
        setLoading(true);

        // Use selected medication from smart dashboard or fallback to prop
        const targetMedication = selectedMed || medication;
        if (!targetMedication) {
            message.error('No medication selected');
            setLoading(false);
            return;
        }

        try {
            // Create a real administration session via API
            const sessionData = {
                medication_id: targetMedication.medication_id,
                patient_id: patient.id,
                patient_mrn: patient.mrn,
                medication_name: targetMedication.medication_name || targetMedication.brand_name,
                scheduled_time: targetMedication.scheduled_time || targetMedication.next_due || new Date().toISOString(),
                // CRITICAL: Include dose schedule identifier for reliable linking
                dose_schedule_id: targetMedication.dose_schedule_id,
                workflow_id: targetMedication.workflow_id
            };

            const response = await fetch(`${config.serverPath}/medpass/administration-sessions`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${JSON.parse(localStorage.getItem('loginSuccessNeurulaHealth'))?.authToken || ''}`
                },
                body: JSON.stringify(sessionData)
            });

            if (response.ok) {
                const data = await response.json();
                message.success('Administration session created successfully');
                
                // Reload sessions to get the updated list
                await loadAdministrationSessions();
                
                // Auto-open the start modal for the newly created session
                console.log('✅ Session created, looking for session to start...', data);
                
                // Open start modal immediately for the new session
                if (data.session_id) {
                    console.log('🚀 Opening start modal for session:', data.session_id);
                    const newSession = {
                        session_id: data.session_id,
                        medication_name: medication.medication_name || medication.brand_name,
                        status: 'created'
                    };
                    setSelectedSession(newSession);
                    setShowStartModal(true);
                }
            } else {
                const errorData = await response.json();
                message.error(errorData.detail || 'Failed to create administration session');
            }
        } catch (error) {
            console.error('Error creating session:', error);
            message.error('Failed to create administration session');
        } finally {
            setLoading(false);
        }
    };

    const handleStartAdministration = async (startData) => {
        if (!selectedSession) return;

        console.log('AdministrationSessionManager - handleStartAdministration called with:', startData);
        console.log('AdministrationSessionManager - Selected session:', selectedSession);

        setLoading(true);
        try {
            const requestBody = JSON.stringify(startData);
            console.log('AdministrationSessionManager - Start request body:', requestBody);
            console.log('AdministrationSessionManager - Start API endpoint:', `${config.serverPath}/medpass/administration-sessions/${selectedSession.session_id}/start`);
            
            const response = await fetch(`${config.serverPath}/medpass/administration-sessions/${selectedSession.session_id}/start`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${JSON.parse(localStorage.getItem('loginSuccessNeurulaHealth'))?.authToken || ''}`
                },
                body: requestBody
            });
            
            console.log('AdministrationSessionManager - Start response status:', response.status);

            if (response.ok) {
                const data = await response.json();
                console.log('AdministrationSessionManager - Start response data:', data);
                message.success('Administration started successfully');
                setShowStartModal(false);
                
                // Reload sessions to get updated data
                await loadAdministrationSessions();
                
                // Update selected session
                const updatedSession = sessions.find(s => s.session_id === selectedSession.session_id);
                if (updatedSession) {
                    setSelectedSession(updatedSession);
                }
            } else {
                const errorData = await response.json();
                message.error(errorData.detail || 'Failed to start administration');
            }
        } catch (error) {
            console.error('Error starting administration:', error);
            message.error('Error starting administration');
        } finally {
            setLoading(false);
        }
    };

    const handleEndAdministration = async (endData) => {
        if (!selectedSession) return;

        console.log('AdministrationSessionManager - handleEndAdministration called with:', endData);
        console.log('AdministrationSessionManager - Selected session:', selectedSession);

        setLoading(true);
        try {
            const requestBody = JSON.stringify(endData);
            console.log('AdministrationSessionManager - Request body:', requestBody);
            console.log('AdministrationSessionManager - API endpoint:', `${config.serverPath}/medpass/administration-sessions/${selectedSession.session_id}/end`);
            
            const response = await fetch(`${config.serverPath}/medpass/administration-sessions/${selectedSession.session_id}/end`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${JSON.parse(localStorage.getItem('loginSuccessNeurulaHealth'))?.authToken || ''}`
                },
                body: requestBody
            });
            
            console.log('AdministrationSessionManager - Response status:', response.status);
            console.log('AdministrationSessionManager - Response headers:', response.headers);

            if (response.ok) {
                const data = await response.json();
                console.log('AdministrationSessionManager - Response data:', data);
                message.success('Administration completed successfully');
                setShowEndModal(false);
                
                // Reload sessions to get updated data
                await loadAdministrationSessions();
                
                // Update selected session
                const updatedSession = sessions.find(s => s.session_id === selectedSession.session_id);
                if (updatedSession) {
                    setSelectedSession(updatedSession);
                }
                
                if (onComplete) {
                    onComplete(updatedSession || data);
                }
            } else {
                const errorData = await response.json();
                message.error(errorData.detail || 'Failed to complete administration');
            }
        } catch (error) {
            console.error('Error completing administration:', error);
            message.error('Error completing administration');
        } finally {
            setLoading(false);
        }
    };

    const cancelSession = async (sessionId, reason) => {
        setLoading(true);
        try {
            const response = await fetch(`${config.serverPath}/medpass/administration-sessions/${sessionId}/cancel`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${JSON.parse(localStorage.getItem('loginSuccessNeurulaHealth'))?.authToken || ''}`
                },
                body: JSON.stringify({
                    cancellation_reason: reason
                })
            });

            if (response.ok) {
                message.success('Administration session cancelled');
                // Reload sessions to get updated data
                await loadAdministrationSessions();
            } else {
                const errorData = await response.json();
                message.error(errorData.detail || 'Failed to cancel session');
            }
        } catch (error) {
            console.error('Error cancelling session:', error);
            message.error('Error cancelling session');
        } finally {
            setLoading(false);
        }
    };

    // Smart medication status helpers
    const getMedicationStatusConfig = (status) => {
        const configs = {
            'pending': { color: 'default', text: 'Pending', icon: <ClockCircleOutlined /> },
            'due_now': { color: 'processing', text: 'Due Now', icon: <ExclamationCircleOutlined /> },
            'overdue': { color: 'warning', text: 'Overdue', icon: <ExclamationCircleOutlined /> },
            'missed': { color: 'error', text: 'Missed', icon: <CloseCircleOutlined /> },
            'administered_on_time': { color: 'success', text: 'Administered', icon: <CheckCircleOutlined /> },
            'administered_early': { color: 'success', text: 'Administered Early', icon: <CheckCircleOutlined /> },
            'administered_late': { color: 'warning', text: 'Administered Late', icon: <CheckCircleOutlined /> },
            'cancelled': { color: 'error', text: 'Cancelled', icon: <CloseCircleOutlined /> }
        };
        return configs[status] || { color: 'default', text: status, icon: <InfoCircleOutlined /> };
    };

    const canAdministerMedication = (med) => {
        return med.can_administer && !med.is_completed;
    };

    const requiresJustification = (status) => {
        return ['administered_early', 'administered_late', 'missed', 'cancelled'].includes(status);
    };

    const getSessionStatusTag = (session) => {
        const statusConfig = {
            'created': { color: 'default', text: 'Created' },
            'started': { color: 'processing', text: 'In Progress' },
            'in_progress': { color: 'processing', text: 'In Progress' },
            'paused': { color: 'warning', text: 'Paused' },
            'completed': { color: 'success', text: 'Completed' },
            'cancelled': { color: 'error', text: 'Cancelled' },
            'failed': { color: 'error', text: 'Failed' }
        };

        const config = statusConfig[session.status] || { color: 'default', text: session.status };
        return <Tag color={config.color}>{config.text}</Tag>;
    };

    const getWindowValidationAlert = (session) => {
        if (!session.scheduled_time) return null;

        const validation = validateAdministrationWindow(session.scheduled_time);
        
        if (!validation.isValid) {
            return (
                <Alert
                    message="Administration Window"
                    description={validation.message}
                    type="error"
                    size="small"
                    showIcon
                    style={{ marginBottom: 8 }}
                />
            );
        }

        return (
            <Alert
                message="Administration Window"
                description={validation.message}
                type="success"
                size="small"
                showIcon
                style={{ marginBottom: 8 }}
            />
        );
    };

    const renderSessionCard = (session) => {
        const canStart = session.status === 'created';
        const canEnd = session.status === 'started' || session.status === 'in_progress';
        const canCancel = !['completed', 'cancelled'].includes(session.status);
        
        const windowValidation = session.scheduled_time ? 
            validateAdministrationWindow(session.scheduled_time, currentTime) : null;

        return (
            <Card 
                key={session.session_id}
                size="small"
                style={{ marginBottom: 16 }}
                title={
                    <Space>
                        <MedicineBoxOutlined />
                        {session.medication_name}
                        {getSessionStatusTag(session)}
                    </Space>
                }
                extra={
                    <Space>
                        {session.handoff_occurred && (
                            <Tooltip title="Nurse handoff occurred">
                                <Badge count="H" style={{ backgroundColor: '#1890ff' }} />
                            </Tooltip>
                        )}
                        
                        <Button 
                            type="link" 
                            size="small"
                            onClick={() => {
                                setSelectedSession(session);
                                setShowDetailsModal(true);
                            }}
                        >
                            Details
                        </Button>
                    </Space>
                }
            >
                {getWindowValidationAlert(session)}
                
                <Descriptions size="small" column={2}>
                    <Descriptions.Item label="Session ID">
                        {session.session_id.substring(0, 8)}...
                    </Descriptions.Item>
                    <Descriptions.Item label="Created">
                        {dayjs(session.created_at).format('hh:mm A')}
                    </Descriptions.Item>

                    {session.scheduled_time && (
                        <Descriptions.Item label="Scheduled">
                            {dayjs(session.scheduled_time).format('hh:mm A')}
                        </Descriptions.Item>
                    )}

                    {session.start_time && (
                        <Descriptions.Item label="Started">
                            {dayjs(session.start_time).format('hh:mm A')} by {session.start_nurse_name}
                        </Descriptions.Item>
                    )}

                    {session.end_time && (
                        <Descriptions.Item label="Completed">
                            {dayjs(session.end_time).format('hh:mm A')} by {session.end_nurse_name}
                        </Descriptions.Item>
                    )}
                    
                    {session.duration_minutes && (
                        <Descriptions.Item label="Duration">
                            {session.duration_minutes} minutes
                        </Descriptions.Item>
                    )}
                </Descriptions>

                {windowValidation && (
                    <div style={{ marginTop: 12 }}>
                        <Alert
                            message={`Timing: ${windowValidation.message}`}
                            type={windowValidation.severity}
                            size="small"
                            showIcon
                        />
                    </div>
                )}

                <div style={{ marginTop: 16 }}>
                    <Space>
                        {canStart && (
                            <Button
                                type="primary"
                                icon={<PlayCircleOutlined />}
                                onClick={() => {
                                    setSelectedSession(session);
                                    setShowStartModal(true);
                                }}
                                disabled={windowValidation && !windowValidation.isValid}
                            >
                                Start Administration
                            </Button>
                        )}
                        
                        {canEnd && (
                            <Button
                                type="primary"
                                icon={<CheckCircleOutlined />}
                                onClick={() => {
                                    setSelectedSession(session);
                                    setShowEndModal(true);
                                }}
                            >
                                Complete Administration
                            </Button>
                        )}
                        
                        {canCancel && (
                            <Button
                                danger
                                icon={<CloseCircleOutlined />}
                                onClick={() => {
                                    Modal.confirm({
                                        title: 'Cancel Administration Session',
                                        content: 'Are you sure you want to cancel this administration session?',
                                        onOk: () => cancelSession(session.session_id, 'Cancelled by user')
                                    });
                                }}
                            >
                                Cancel
                            </Button>
                        )}
                    </Space>
                </div>
            </Card>
        );
    };

    const hasActiveSessions = sessions.some(s => ['created', 'started', 'in_progress'].includes(s.status));
    const canCreateSession = !hasActiveSessions;

    const renderMedicationDashboard = () => {
        if (!dashboardSummary) {
            return <div>Loading medication dashboard...</div>;
        }

        return (
            <Card title="Medication Dashboard" style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 16 }}>
                    <Space size="large">
                        <Badge count={dashboardSummary.due_now} color="orange">
                            <span>Due Now</span>
                        </Badge>
                        <Badge count={dashboardSummary.overdue} color="red">
                            <span>Overdue</span>
                        </Badge>
                        <Badge count={dashboardSummary.pending} color="blue">
                            <span>Pending</span>
                        </Badge>
                        <Badge count={dashboardSummary.completed_today} color="green">
                            <span>Completed Today</span>
                        </Badge>
                    </Space>
                </div>

                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {visibleMedications.map((med, index) => {
                        const statusConfig = getMedicationStatusConfig(med.dynamic_status);
                        const canAdminister = canAdministerMedication(med);

                        return (
                            <Card
                                key={`${med.medication_id}-${index}`}
                                size="small"
                                style={{
                                    marginBottom: 8,
                                    border: canAdminister ? '2px solid #1890ff' : '1px solid #d9d9d9',
                                    backgroundColor: canAdminister ? '#f6ffed' : 'white'
                                }}
                            >
                                <Row justify="space-between" align="middle">
                                    <Col span={12}>
                                        <div>
                                            <strong>{med.medication_name}</strong>
                                            <div style={{ fontSize: '12px', color: '#666' }}>
                                                {med.dose_amount} | {med.route} | {dayjs(med.scheduled_time).format('DD/MM/YYYY hh:mm A')}
                                            </div>
                                        </div>
                                    </Col>
                                    <Col span={6}>
                                        <Tag color={statusConfig.color} icon={statusConfig.icon}>
                                            {statusConfig.text}
                                        </Tag>
                                    </Col>
                                    <Col span={6} style={{ textAlign: 'right' }}>
                                        {canAdminister && (
                                            <Button
                                                type="primary"
                                                size="small"
                                                onClick={() => {
                                                    setSelectedMedication(med);
                                                    createAdministrationSession(med);
                                                }}
                                                icon={<PlayCircleOutlined />}
                                            >
                                                Start
                                            </Button>
                                        )}
                                        {med.is_completed && (
                                            <Button
                                                size="small"
                                                onClick={() => {
                                                    // Show medication details
                                                    setSelectedMedication(med);
                                                    setShowDetailsModal(true);
                                                }}
                                            >
                                                View
                                            </Button>
                                        )}
                                    </Col>
                                </Row>
                            </Card>
                        );
                    })}
                </div>
            </Card>
        );
    };

    // Debug logging
    console.log('🔧 AdministrationSessionManager render:', { 
        visible, 
        patient: patient?.full_name, 
        medication: medication?.medication_name,
        showStartModal,
        showEndModal,
        showDetailsModal 
    });

    if (!visible) return null;

    return (
        <div className="administration-session-manager">
                {/* Smart Medication Dashboard */}
                {renderMedicationDashboard()}

                <Card
                    extra={
                        <Space>
                            <AdministrationHistoryButton
                                patientId={patient?.id || patient?.patient_id}
                                patientName={patient?.full_name || patient?.name || 'Patient'}
                                size="default"
                                type="default"
                            />
                            <Button
                                type="primary"
                                icon={<PlayCircleOutlined />}
                                onClick={createAdministrationSession}
                                disabled={!canCreateSession || loading}
                                loading={loading}
                            >
                                New Administration Session
                            </Button>
                        </Space>
                    }
                >
                <Alert
                    message="Two-Step Administration Process"
                    description="Start and end administration can be performed by different nurses. Each medication administration must be within 24 hours of scheduled time."
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                />

                {!canCreateSession && (
                    <Alert
                        message="Active Session"
                        description="Complete or cancel the current session before starting a new one."
                        type="warning"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                )}

                <Spin spinning={loading}>
                    {sessions.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px 0' }}>
                            <MedicineBoxOutlined style={{ fontSize: '48px', color: '#ccc' }} />
                            <div style={{ marginTop: 16, color: '#999' }}>
                                No started administration sessions
                            </div>
                            <div style={{ marginTop: 8, color: '#999', fontSize: '14px' }}>
                                Create a new session and start it to appear here
                            </div>
                        </div>
                    ) : (
                        <div>
                            {sessions
                                .filter(s => ['started', 'in_progress'].includes(s.status))
                                .map(renderSessionCard)}
                        </div>
                    )}
                </Spin>
                </Card>

                {/* Modals rendered directly without portals to test */}
                <StartAdministrationModal
                    open={showStartModal}
                    session={selectedSession}
                    patient={patient}
                    medication={medication}
                    onStart={handleStartAdministration}
                    onCancel={() => {
                        setShowStartModal(false);
                        setSelectedSession(null);
                    }}
                        zIndex={15000}
                />

                <EndAdministrationModal
                    open={showEndModal}
                    session={selectedSession}
                    patient={patient}
                    medication={medication}
                    onEnd={handleEndAdministration}
                    onCancel={() => {
                        setShowEndModal(false);
                        setSelectedSession(null);
                    }}
                        zIndex={15000}
                />

                <SessionDetailsModal
                    open={showDetailsModal}
                    session={selectedSession}
                    patient={patient}
                    medication={medication}
                    onClose={() => {
                        setShowDetailsModal(false);
                        setSelectedSession(null);
                    }}
                        zIndex={15000}
                />
        </div>
    );
};

export default AdministrationSessionManager;